import React, { useEffect, useState } from 'react';
import { useTranslate } from 'react-translate-plus';
import styled from 'styled-components';

import { getUserProfileById } from '../../api/user-profile/getUserProfileById';
import { ConfigurationStateText } from '../../components/ConfigurationStateText/ConfigurationStateText';
import { useAsync } from '../../hooks/useAsync';
import { ConfigurationState } from '../../projects/services/projects.service';
import { toShortDateTimeFromString } from '../../projects/utils/date';
import ClipboardCopy from '../ClipboardCopy/ClipboardCopy';

interface Props {
  configurationState?: string;
  requestInfo?: SimpleProjectServiceAPI.ConfigurationRequestDto;
}

const ANONYMOUS_USER = 'anonymousUser';

export const CustomerInformation: React.FC<Props> = ({ requestInfo, configurationState }) => {
  const t = useTranslate('ContactFormInfoModal');

  const [requestedByName, setRequestedByName] = useState<string>('');
  const [
    contactFormBusinessEvent,
    setContactFormBusinessEvent,
  ] = useState<SimpleProjectServiceAPI.ContactFormBusinessEvent>({});
  const { start: loadRequestedByUser, result: requestedByUser } = useAsync(getUserProfileById);

  useEffect(() => {
    const createdBy = requestInfo?.createdBy;
    if (createdBy !== ANONYMOUS_USER && !requestedByUser) {
      loadRequestedByUser(createdBy);
    }
  }, [requestInfo, loadRequestedByUser, requestedByUser]);

  useEffect(() => {
    if (requestedByUser) {
      setRequestedByName(`${requestedByUser.firstName} ${requestedByUser.lastName}`);
    }
  }, [requestedByUser]);

  useEffect(() => {
    if (requestInfo && requestInfo.events && requestInfo.events.length > 0) {
      requestInfo.events.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
      setContactFormBusinessEvent(jsonSafeParse(requestInfo.events[0].payload));
    }
  }, [requestInfo]);

  const jsonSafeParse = (payload: string) => {
    try {
      return JSON.parse(payload);
    } catch (e) {
      return undefined;
    }
  };

  const { payload = {}, createdAt = null, sapOffer = {} } = requestInfo || {};
  const {
    city = '',
    countryName = '',
    email = '',
    firstName = '',
    lastName = '',
    message = '',
    parameters = { phone: '' },
    zip = '',
  } = payload as any;
  const customerName = sapOffer?.customerName || (payload as any).customerName || '';
  const customerNumber = sapOffer?.customerNumber || (payload as any).customerNumber || '';
  const offerNumber = sapOffer?.offerNumber;
  const orderNumber = sapOffer?.orderNumber;
  const fullName = `${firstName || ''} ${lastName || ''}`.trim();

  return (
    <Container>
      {configurationState && (
        <Row>
          <Label>{t('LABEL_STATUS')}:</Label>
          <Value>
            <ConfigurationStateText state={ConfigurationState[configurationState]} />
          </Value>
        </Row>
      )}
      <Row>
        <Label>{t('LABEL_REQUEST_FROM')}:</Label>
        <Value>{toShortDateTimeFromString(createdAt)}</Value>
      </Row>
      <Row>
        <Label>{t('LABEL_SENT_TO')}:</Label>
        <Value>
          {contactFormBusinessEvent.distributorDisplayKey ||
            contactFormBusinessEvent.distributor ||
            ''}{' '}
          -{' '}
          {contactFormBusinessEvent.distributorDisplayName ||
            contactFormBusinessEvent.distributorName ||
            ''}
        </Value>
      </Row>
      <Row>
        <Label></Label>
        <Value>({contactFormBusinessEvent.salesContactEmail || '-'})</Value>
      </Row>
      <Row>
        <Label>{t('LABEL_SAP_OFFER')}:</Label>
        <Value>
          <TextWithCopyIcon>
            <div>{offerNumber || '-'}</div>
            {offerNumber && (
              <div>
                <ClipboardCopy value={offerNumber.trim()} />
              </div>
            )}
          </TextWithCopyIcon>
        </Value>
      </Row>
      <Row>
        <Label>{t('LABEL_SAP_ORDER')}:</Label>
        <Value>
          <TextWithCopyIcon>
            <div>{orderNumber || '-'}</div>
            {orderNumber && (
              <div>
                <ClipboardCopy value={orderNumber.trim()} />
              </div>
            )}
          </TextWithCopyIcon>
        </Value>
      </Row>
      <Row>
        <Label>{t('LABEL_REQUESTED_BY')}:</Label>
        <Value>{requestedByName || '-'}</Value>
      </Row>
      <Row>
        <Label>{t('LABEL_CUSTOMER_NUMBER')}:</Label>
        <Value>
          <TextWithCopyIcon>
            <div>{customerNumber || '-'}</div>
            {customerNumber && (
              <div>
                <ClipboardCopy value={customerNumber.trim()} />
              </div>
            )}
          </TextWithCopyIcon>
        </Value>
      </Row>
      <Row>
        <Label>{t('LABEL_CUSTOMER_NAME')}:</Label>
        <Value>{customerName || '-'}</Value>
      </Row>
      <Row>
        <Label>{t('LABEL_FULLNAME')}:</Label>
        <Value>{fullName || '-'}</Value>
      </Row>
      <Row>
        <Label>{t('LABEL_ADDRESS')}:</Label>
        <Value>{`${zip} ${city}`.trim() || '-'} </Value>
      </Row>
      <Row>
        <Label>{t('LABEL_COUNTRY')}:</Label>
        <Value>{countryName || '-'}</Value>
      </Row>
      <Row>
        <Label>{t('LABEL_EMAIL')}:</Label>
        <Value>{email || '-'}</Value>
      </Row>
      <Row>
        <Label>{t('LABEL_PHONE')}:</Label>
        <Value>{parameters?.phone || '-'}</Value>
      </Row>

      <Row>
        <Label>{t('LABEL_MESSAGE')}:</Label>
        <Value>{message || '-'}</Value>
      </Row>
    </Container>
  );
};

const Container = styled.div``;

const Row = styled.div`
  display: flex;
  flex-flow: row nowrap;
  margin-top: 0;
  margin-bottom: 0.5rem;
`;

const Label = styled.div`
  word-break: break-word;
  padding-right: 1rem;
  flex: 0 0 30%;
`;
const Value = styled.div`
  font-weight: bold;
  flex: 0 0 65%;
  word-break: break-word;
`;

const TextWithCopyIcon = styled.div`
  align-items: center;
  display: flex;
  flex-flow: row nowrap;

  svg {
    margin-left: 8px;
    width: 20px;
    height: 20px;
  }
`;
